// SofortTipp.tsx — Tooltip ohne Verzögerung (reines CSS, kein natives `title`).
//
// Das native `title` erscheint erst nach rund einer Sekunde und nie bei
// Tastatur-Fokus. Für die Beziehungslisten (Status-Chip, Aktions-Icons) soll
// der Text sofort beim Überfahren stehen und auch beim Fokussieren mit der
// Tastatur (Plan 2026-10-01 Verbindungen/Beziehungen, Paket A1).
//
// Der Text steht als eigenes Element über dem Inhalt (`data-sofort-tipp`) und
// ist für Screenreader verborgen — den Namen liefert schon `aria-label` bzw.
// der sichtbare Inhalt.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";

const sichtbar = { opacity: 1, visibility: "visible" as const };

const useStyles = makeStyles({
  huelle: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    minWidth: 0,
  },
  zeigen: {
    ":hover > [data-sofort-tipp]": sichtbar,
    ":focus-within > [data-sofort-tipp]": sichtbar,
  },
  tipp: {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    zIndex: 30,
    width: "max-content",
    maxWidth: "280px",
    ...shorthands.padding("5px", "9px"),
    ...shorthands.borderRadius("7px"),
    backgroundColor: REFINED.ink,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "12.5px",
    fontWeight: 600,
    lineHeight: 1.35,
    whiteSpace: "normal",
    overflowWrap: "break-word",
    textAlign: "left",
    textDecorationLine: "none",
    boxShadow: "0 4px 14px rgba(36,21,34,.22)",
    pointerEvents: "none",
    opacity: 0,
    visibility: "hidden",
  },
  links: { left: 0 },
  rechts: { right: 0 },
});

export function SofortTipp({
  text,
  ausrichtung = "links",
  nurWennGekuerzt,
  className,
  children,
}: {
  text: string;
  /** Tipp am linken oder rechten Rand des Inhalts ausrichten (rechts: Aktionsspalte). */
  ausrichtung?: "links" | "rechts";
  /**
   * Nur zeigen, wenn der Inhalt (das erste Kind-Element) abgeschnitten ist.
   * Der Inhalt wird dann per Tastatur fokussierbar, damit der volle Text auch
   * ohne Maus erreichbar ist.
   */
  nurWennGekuerzt?: boolean;
  className?: string;
  children: React.ReactElement<{ ref?: React.Ref<HTMLElement>; tabIndex?: number }>;
}) {
  const s = useStyles();
  const ref = React.useRef<HTMLElement | null>(null);
  const [gekuerzt, setGekuerzt] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!nurWennGekuerzt) return;
    const el = ref.current;
    if (!el) return;
    const pruefen = () => setGekuerzt(el.scrollWidth > el.clientWidth + 1);
    pruefen();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(pruefen);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nurWennGekuerzt, text]);

  const zeigen = !nurWennGekuerzt || gekuerzt;
  const kind = nurWennGekuerzt
    ? React.cloneElement(children, { ref, tabIndex: gekuerzt ? 0 : undefined })
    : children;

  return (
    <span className={mergeClasses(s.huelle, zeigen && s.zeigen, className)} data-gekuerzt={nurWennGekuerzt ? gekuerzt : undefined}>
      {kind}
      {/* Nur im DOM, wenn er etwas zu sagen hat: Ein Status, der ganz
          hineinpasst, stünde sonst doppelt im Text der Zeile. */}
      {zeigen && (
        <span data-sofort-tipp="" aria-hidden className={mergeClasses(s.tipp, ausrichtung === "rechts" ? s.rechts : s.links)}>
          {text}
        </span>
      )}
    </span>
  );
}
